import React, { useState, useEffect } from 'react'
import { T, Spinner, Empty, SearchBar, searchMatch, inGameFilter } from '../ui'
import DataGrid, { nameCell } from '../grids'
import { getNFLGrid } from '../api'
import PicksTab from './PicksTab'
import { teamNick } from '../plain'

const f1 = v => (typeof v === 'number' ? v.toFixed(1) : v)
const f2 = v => (typeof v === 'number' ? v.toFixed(2) : v)
// Backend already sends these percent-scaled (100*…), so just append "%" — never ×100.
const pc0 = v => (typeof v === 'number' ? `${Math.round(v)}%` : v)
const pc1 = v => (typeof v === 'number' ? `${v.toFixed(1)}%` : v)

// Column sets — only metrics we compute truthfully from nflverse aggregation
// (season-to-date per game) + projections. Advanced columns (EPA, pressure%,
// YPRR, snap%) arrive with the NGS/PFF enrichment layer.
const ytg = (v, r) => f1(v != null ? v : (r.tgt ? r.yds / r.tgt : 0))
const dash = v => (v == null ? <span style={{opacity:.4}}>—</span> : f1(v))  // NGS may be absent until ingested
// Projected-value formatters (our model line). Null => a dash, so a below-threshold /
// unboarded player never reads as a hard 0. Bold + green heat marks them as OUR numbers.
const pWhole = v => (v == null ? <span style={{opacity:.4}}>—</span> : <b>{Math.round(v)}</b>)   // proj yards
const pOne   = v => (v == null ? <span style={{opacity:.4}}>—</span> : <b>{f1(v)}</b>)            // proj rec / pass TD
const pPct   = v => (v == null ? <span style={{opacity:.4}}>—</span> : <b>{`${Math.round(v)}%`}</b>) // anytime-TD %
const COLS = {
  // Each position LEADS with its projected line (Proj Pts + the model's expected stat
  // line for that position), then season-to-date form.
  QB: [
    { key:'n', label:'Player', left:1, fmt:nameCell },
    { key:'pts', label:'Proj Pts', heat:{min:6,max:22}, fmt:v=>v==null?<span style={{opacity:.4}}>—</span>:<b>{f1(v)}</b> },
    { key:'p_pyd', label:'Proj Pass', heat:{min:180,max:300}, fmt:pWhole },
    { key:'p_ptd', label:'Proj PaTD', heat:{min:0.5,max:2.5}, fmt:pOne },
    { key:'p_ruyd', label:'Proj Rush', heat:{min:0,max:60}, fmt:pWhole },
    { key:'att', label:'Att', fmt:f1 }, { key:'cmp', label:'Cmp', fmt:f1 }, { key:'cmpp', label:'Cmp%', fmt:pc1 },
    { key:'yds', label:'Yds/G', heat:{min:180,max:300} }, { key:'ypa', label:'YPA', fmt:f1 },
    { key:'td', label:'TD', fmt:f1 }, { key:'int', label:'INT', fmt:f1 },
    { key:'ruyd', label:'Rush Y/G', heat:{min:0,max:55} }, { key:'rutd', label:'Rush TD', fmt:f1 },
    { key:'adot', label:'aDOT', fmt:dash, adv:1 },
    { key:'ttt', label:'TTT', fmt:dash, adv:1 }, { key:'cpoe', label:'CPOE', fmt:dash, adv:1 },
  ],
  RB: [
    { key:'n', label:'Player', left:1, fmt:nameCell },
    { key:'pts', label:'Proj Pts', heat:{min:6,max:22}, fmt:v=>v==null?<span style={{opacity:.4}}>—</span>:<b>{f1(v)}</b> },
    { key:'p_ruyd', label:'Proj Rush', heat:{min:30,max:110}, fmt:pWhole },
    { key:'p_rec', label:'Proj Rec', heat:{min:2,max:6}, fmt:pOne },
    { key:'p_ryd', label:'Proj RecYd', heat:{min:10,max:55}, fmt:pWhole },
    { key:'p_td', label:'TD %', heat:{min:10,max:55}, fmt:pPct },
    { key:'att', label:'Car', fmt:f1 }, { key:'ryd', label:'Rush Yds/G', heat:{min:30,max:110} },
    { key:'ypc', label:'YPC', fmt:f1 }, { key:'ryoe', label:'RYOE', fmt:dash, adv:1 }, { key:'td', label:'TD', fmt:f1 },
    { key:'tgt', label:'Tgt', fmt:f1 }, { key:'rec', label:'Rec', fmt:f1 },
  ],
  WR: [
    { key:'n', label:'Player', left:1, fmt:nameCell },
    { key:'pts', label:'Proj Pts', heat:{min:6,max:22}, fmt:v=>v==null?<span style={{opacity:.4}}>—</span>:<b>{f1(v)}</b> },
    { key:'p_rec', label:'Proj Rec', heat:{min:3,max:8}, fmt:pOne },
    { key:'p_ryd', label:'Proj Yds', heat:{min:30,max:100}, fmt:pWhole },
    { key:'p_td', label:'TD %', heat:{min:8,max:45}, fmt:pPct },
    { key:'tgt', label:'Tgt', fmt:f1 }, { key:'rec', label:'Rec', fmt:f1 },
    { key:'yds', label:'Yds/G', heat:{min:30,max:100} }, { key:'ytg', label:'Y/Tgt', fmt:ytg },
    { key:'adot', label:'aDOT', fmt:f1 }, { key:'sep', label:'Sep', fmt:dash, adv:1 }, { key:'yac', label:'YAC', fmt:dash, adv:1 },
    { key:'tgts', label:'Tgt%', fmt:pc0 }, { key:'td', label:'TD', fmt:f1 },
  ],
  TE: [
    { key:'n', label:'Player', left:1, fmt:nameCell },
    { key:'pts', label:'Proj Pts', heat:{min:6,max:22}, fmt:v=>v==null?<span style={{opacity:.4}}>—</span>:<b>{f1(v)}</b> },
    { key:'p_rec', label:'Proj Rec', heat:{min:2,max:6.5}, fmt:pOne },
    { key:'p_ryd', label:'Proj Yds', heat:{min:20,max:70}, fmt:pWhole },
    { key:'p_td', label:'TD %', heat:{min:8,max:40}, fmt:pPct },
    { key:'tgt', label:'Tgt', fmt:f1 }, { key:'rec', label:'Rec', fmt:f1 },
    { key:'yds', label:'Yds/G', heat:{min:20,max:65} }, { key:'ytg', label:'Y/Tgt', fmt:ytg },
    { key:'adot', label:'aDOT', fmt:f1 }, { key:'sep', label:'Sep', fmt:dash, adv:1 }, { key:'yac', label:'YAC', fmt:dash, adv:1 },
    { key:'tgts', label:'Tgt%', fmt:pc0 }, { key:'td', label:'TD', fmt:f1 },
  ],
}
const POS = ['QB', 'RB', 'WR', 'TE']

// The player stat grids (season-to-date per game + projections), by position.
function PlayersGrid({ season, week, onPlayer, pro, gameFilter }) {
  const [pos, setPos] = useState('WR')
  const [rows, setRows] = useState(null)
  const [loading, setLoading] = useState(true)
  const [sample, setSample] = useState(false)
  const [q, setQ] = useState('')

  useEffect(() => {
    let alive = true; setLoading(true)
    getNFLGrid(pos, season, week).then(res => {
      if (!alive) return
      setRows(res.rows || []); setSample(!!res._sample); setLoading(false)
    })
    return () => { alive = false }
  }, [pos, season, week])

  return (
    <div style={{ padding: '0 16px 80px' }}>
      <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
        {POS.map(p => {
          const on = p === pos
          return (
            <button key={p} onClick={() => setPos(p)} style={{
              padding: '7px 15px', borderRadius: 20, cursor: 'pointer',
              background: on ? T.green : T.card, color: on ? '#04140D' : T.muted,
              border: `1px solid ${on ? T.green : T.border}`, fontSize: 12, fontWeight: 800,
            }}>{p}</button>
          )
        })}
      </div>
      <div style={{ fontSize: 10.5, color: T.muted, margin: '2px 0 10px', lineHeight: 1.5 }}>
        {sample ? <span style={{color:T.amber}}>● Live data unavailable — </span> : ''}
        <b style={{color:T.green}}>Proj</b> columns are our model’s projected line for this week (catches, yards, TD% — rushing for RBs, passing for QBs); the rest is season-to-date per game. <b style={{color:T.text}}>Tap a column</b> to sort, <b style={{color:T.text}}>a row</b> for the full breakdown.
        <span style={{opacity:.7}}> “—” in a Proj column = below the model’s line threshold. Early in the season the per-game stats reflect <b style={{color:T.muted2}}>last season’s form</b> until this year’s games bank.</span>
      </div>
      {rows?.length > 0 && <SearchBar value={q} onChange={setQ} />}
      {loading ? <Spinner />
        : !rows?.length ? <Empty message="No grid data yet" />
        : (() => { const shown = rows.filter(searchMatch(q, r => [r.n, r.team, r.opp])).filter(r => inGameFilter(gameFilter, r.team))
          // Advanced (NGS/PFF) columns show only in Pro mode, and only once their metric
          // has data — Simple keeps the grid clean; an all-empty column reads as broken.
          const cols = COLS[pos].filter(c => !c.adv || (pro && shown.some(r => r[c.key] != null)))
          return !shown.length ? <Empty message={`No players match "${q}"`} />
        : <DataGrid cols={cols} rows={shown} initialSort="pts"
            onRow={r => onPlayer && onPlayer({
              full_name: r.n, pos, team: r.team, opp: r.opp,
              // Only carry a bet if this row actually has a projected prop; a bare stat
              // row must open the player-info view, not a fake "0% / NaN" bet card.
              ...(r.prob != null && r.line != null
                ? { prob_over: r.prob / 100, line: r.line, market: r.market || 'Receptions' }
                : {}),
              note: `${pos} matchup vs ${teamNick(r.opp)}`, status: 'active',
            })} /> })()}
    </div>
  )
}

// Players hub — one home for the player board: the model Rankings, the season-to-date
// stat grid, and the defense-vs-position matchup grid. (TD Lab and Trends moved to the
// More tab to keep this focused on players.) `initialMode` lets Home deep-link to Rankings.
const MODES = [['rankings', 'Rankings'], ['stats', 'Stats']]   // Defense moved into Games → Attack

export default function GridsTab({ season, week, onPlayer, pro, initialMode, initialMarket, gameFilter }) {
  // A deep-linked market (from a Home market row) forces the Rankings mode so it lands
  // on that market's board rather than the default.
  const [mode, setMode] = useState(initialMarket ? 'rankings'
    : (initialMode && MODES.some(([id]) => id === initialMode) ? initialMode : 'rankings'))
  return (
    <div>
      <div style={{ display: 'flex', gap: 6, padding: '12px 16px 8px' }}>
        {MODES.map(([id, label]) => {
          const on = id === mode
          return (
            <button key={id} onClick={() => setMode(id)} style={{
              flex: 1, padding: '9px 0', borderRadius: 9, cursor: 'pointer', fontWeight: 800, fontSize: 13,
              border: `1px solid ${on ? T.green : T.border}`, background: on ? T.greenDim : T.card,
              color: on ? T.green : T.muted,
            }}>{label}</button>
          )
        })}
      </div>
      {mode === 'rankings' && <PicksTab season={season} week={week} onPlayer={onPlayer} initialMarket={initialMarket} gameFilter={gameFilter} />}
      {mode === 'stats' && <PlayersGrid season={season} week={week} onPlayer={onPlayer} pro={pro} gameFilter={gameFilter} />}
    </div>
  )
}
